import LocalConfigContext from "components/context/LocalConfigContext";
import { useContext } from "react";
import { getUsersProfile } from "store/api/apiSlice";
import styled from "styled-components";
import { CardNetwork } from "types/cardNetwork";
import { ApiUsersProduct } from "utils/api/users";
import { getPartnerConfig } from "utils/partner";

const Wrapper = styled.div`
  position: relative;

  padding-bottom: 63%;

  color: ${(props) => props.theme.colors.textReverse};

  font-family: "JetBrains Mono", Courier, monospace;

  container-type: size;
  container-name: dashboard-card;
`;

const Background = styled.img`
  position: absolute;
  top: 0;
  left: 0;

  width: 100%;
  height: 100%;

  object-fit: cover;
`;

const Inner = styled.div`
  position: absolute;
  top: 0;
  left: 0;

  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;

  @container dashboard-card (min-width: 1px) {
    padding: 5cqw;

    border-radius: 3cqw;
    -webkit-mask-image: -webkit-radial-gradient(white, black);

    font-size: 5cqw;
    line-height: 1;
  }
`;

const Top = styled.div`
  position: relative;

  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const Bottom = styled.div`
  position: relative;

  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const Logo = styled.img`
  width: 100%;

  @container dashboard-card (min-width: 1px) {
    max-height: 12cqw;
    max-width: 50cqw;

    object-fit: contain;
    object-position: top left;
  }
`;

const NetworkLogo = styled.img`
  width: 100%;

  @container dashboard-card (min-width: 1px) {
    max-height: 20cqw;
    max-width: 20cqw;

    object-fit: contain;
    object-position: bottom right;
  }
`;

const VisaLogo = () => (
  <NetworkLogo src="/images/card-network/visa-reverse.svg" alt="Visa" />
);

const VisaDebitLogo = () => (
  <NetworkLogo
    src="/images/card-network/visa-debit-reverse.svg"
    alt="Visa Debit"
  />
);

const MastercardLogo = () => (
  <NetworkLogo src="/images/card-network/mastercard.svg" alt="Mastercard" />
);

const MastercardDebitLogo = () => (
  <NetworkLogo
    src="/images/card-network/mastercard-debit-reverse.svg"
    alt="Mastercard Debit"
  />
);

const CurrentNetworkLogo = ({
  product,
  network,
}: {
  product?: ApiUsersProduct;
  network?: CardNetwork;
}) => {
  if (product === ApiUsersProduct.ewaDirect) {
    switch (network) {
      case CardNetwork.mastercard:
        return <MastercardLogo />;
      default:
        return <VisaLogo />;
    }
  }

  switch (network) {
    case CardNetwork.mastercard:
      return <MastercardDebitLogo />;
    default:
      return <VisaDebitLogo />;
  }
};

const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5em;
`;

const Card = () => {
  const config = useContext(LocalConfigContext);
  const partner = getPartnerConfig();
  const { data: profile } = getUsersProfile();
  const { name, lastFour } = profile || {};

  return (
    <Wrapper>
      <Inner>
        <Background
          src={`/partner/${partner.slug}/card-bg.svg`}
          alt="Card Background"
        />

        <Top>
          <Logo
            src={`/partner/${partner.slug}/card-logo.svg`}
            alt={partner.name}
          />
        </Top>

        <Bottom>
          <CardInfo>
            <span>{name}</span>
            {config?.showLastFour ? <span>••••&nbsp;{lastFour}</span> : null}
          </CardInfo>

          <CurrentNetworkLogo
            product={profile?.product}
            network={config.cardNetwork}
          />
        </Bottom>
      </Inner>
    </Wrapper>
  );
};

export default Card;
